import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { FilterBlock, FilterStateItem, Button } from "@racoon/ui";

export type BunkerTypeStatus = "active" | "archived";

interface BunkerTypeStatusFilterProps {
  activeStatus: BunkerTypeStatus;
  onStatusChange: (status: BunkerTypeStatus) => void;
  counts: {
    active: number;
    archived: number;
  };
  onCreateClick: () => void;
  onEditClick?: () => void;
  onArchiveClick?: () => void;
  onRestoreClick?: () => void;
}

const COUNT_MAP: Record<BunkerTypeStatus, keyof BunkerTypeStatusFilterProps["counts"]> = {
  active: "active",
  archived: "archived",
};

export const BunkerTypeStatusFilter: React.FC<BunkerTypeStatusFilterProps> = ({
  activeStatus,
  onStatusChange,
  counts,
  onCreateClick,
  onEditClick,
  onArchiveClick,
  onRestoreClick,
}) => {
  const t = useT();

  const states: FilterStateItem[] = useMemo(
    () => [
      {
        key: t(Namespace.Settings, "bunkerType.status.active"),
        value: "active",
      },
      {
        key: t(Namespace.Settings, "bunkerType.status.archived"),
        value: "archived",
      },
    ],
    [t],
  );

  const selectedState = useMemo(
    () => states.find((s) => s.value === activeStatus) || states[0],
    [states, activeStatus],
  );

  const handleStateChange = (state: FilterStateItem) => {
    onStatusChange(state.value as BunkerTypeStatus);
  };

  const totalCount = counts[COUNT_MAP[activeStatus]] || 0;

  return (
    <FilterBlock
      states={states}
      selectedState={selectedState}
      onStateChange={handleStateChange}
      direction="row"
      totalCount={totalCount}
      bgColor="white"
      additionalButtonsPosition="bottom"
      additionalButtons={
        <>
          {onArchiveClick && (
            <Button size="sm" variant="opacityBlueWithOutline" onClick={onArchiveClick}>
              {t(Namespace.Buttons, "archive")}
            </Button>
          )}
          {onRestoreClick && (
            <Button size="sm" variant="opacityBlueWithOutline" onClick={onRestoreClick}>
              {t(Namespace.Buttons, "restore")}
            </Button>
          )}
          {onEditClick && (
            <Button size="sm" variant="opacityBlueWithOutline" onClick={onEditClick}>
              {t(Namespace.Buttons, "edit")}
            </Button>
          )}
          <Button size="sm" variant="opacityBlueWithOutline" onClick={onCreateClick}>
            {t(Namespace.Buttons, "add")}
          </Button>
        </>
      }
    />
  );
};
